import { FirebaseAppFirestore } from '@/firebase/firebase-app';
import { createDocumentRef, updateDocumentRef } from '@/utils/db-hooks';
import firebaseNames from '@/statics/firebase-names';
import { BillingPhoneNumber, CompanyProfile } from '@/types/company-profile';
import { Nullable } from '@/types/misc';
import { getCurrentUser } from '@/firebase/firebase-user';
import { BillingContact } from '@/types/company-profile';
import { RecentBillingContact } from '@/types/payment';
import firebase from 'firebase';

/**
 * Get company for specific User by his ID.
 * @param uid User ID
 */
export const getCompanyProfileByUserIdAction = async (
  uid: string
): Promise<Nullable<CompanyProfile>> => {
  const dataRef = await FirebaseAppFirestore.collection(
    firebaseNames.COMPANY_PROFILES
  )
    .doc(uid)
    .get();
  const dataDoc = dataRef.data();
  return dataDoc ? (dataDoc as CompanyProfile) : null;
};

/**
 * Get company profiles action.
 */
 export const loadCompanyProfilesAction = async () => {
  const dataRef = await FirebaseAppFirestore.collection(firebaseNames.COMPANY_PROFILES).get();
  const companyProfiles = dataRef.docs.map((snapshot) => {
    return {...snapshot.data(), USER_ID: snapshot.id };
  });
  return companyProfiles as CompanyProfile[];
};

/**
 * Load billing by email
 */
const loadBillingContactByEmail = async (email: string): Promise<BillingContact> => {
  const currentUser = await getCurrentUser();
  const billingContactSnap = await FirebaseAppFirestore
    .collection(firebaseNames.COMPANY_PROFILES)
    .doc(currentUser!.uid)
    .collection(firebaseNames.BILLING_CONTACTS)
    .where('EMAIL', '==', email)
    .get();
  const [recentBillingContact] = billingContactSnap.docs.map((doc) => doc.data() as BillingContact);
  return recentBillingContact;
};

/**
 * Create billing contact
 * @param data RecentBillingContact
 */
const createBillingContact = async ({ email: EMAIL, phone: VALUE }: RecentBillingContact): Promise<void> => {
  const currentUser = await getCurrentUser();
  const billingContactsCollection = FirebaseAppFirestore
    .collection(firebaseNames.COMPANY_PROFILES)
    .doc(currentUser!.uid)
    .collection(firebaseNames.BILLING_CONTACTS);
    
  const contactData = {
    ...EMAIL && { EMAIL },
    ...VALUE && {
      PHONES: [
        {
          VALUE,
          CREATED_AT: firebase.firestore.Timestamp.now(),
        },
      ]
    },
  };
  
  await createDocumentRef(billingContactsCollection.doc(), contactData);
};

const updateBillingContactPhonesList = (billingContact: BillingContact, phone: string): BillingPhoneNumber[] => {
  const isPhoneExists = billingContact && Boolean(billingContact.PHONES?.find((billingPhoneNumber) => billingPhoneNumber.VALUE === phone));
  const VALUE = phone;
  const CREATED_AT = firebase.firestore.Timestamp.now();
  return isPhoneExists
    ? billingContact.PHONES
    : [
      {
        VALUE,
        CREATED_AT,
      },
      ...(billingContact.PHONES || []),
    ];
};

/**
 * Update billing contact phone
 * @param billingContact BillingContact
 */
const updateBillingContact = async (billingContact: BillingContact): Promise<void> => {
  const currentUser = await getCurrentUser();
  const { ID } = billingContact;
  
  if (!ID) {
    throw new Error('Billing contact ID is required for update operation');
  }
  
  const billingContactsCollection = FirebaseAppFirestore
    .collection(firebaseNames.COMPANY_PROFILES)
    .doc(currentUser!.uid)
    .collection(firebaseNames.BILLING_CONTACTS);

  await updateDocumentRef(
    billingContactsCollection.doc(ID),
    billingContact
  );
};
/**
 * Update recent billing contact
 * @param data RecentBillingContact
 */
export const updateRecentBillingContact = async ({ email = '', phone = '' }: RecentBillingContact): Promise<void> => {
  // Early return if neither email nor phone is provided
  if (!email && !phone) {
    return;
  }

  // Handle email-based contact operations
  if (email) {
    return handleEmailBasedContact(email, phone);
  }

  // Handle phone-only contact creation
  if (phone) {
    return createBillingContact({ email, phone });
  }
};

/**
 * Handle operations for email-based billing contact
 * @param email Contact email
 * @param phone Optional contact phone
 */
const handleEmailBasedContact = async (email: string, phone = ''): Promise<void> => {
  const billingContact = await loadBillingContactByEmail(email);

  // Create new contact if it doesn't exist
  if (!billingContact) {
    return createBillingContact({ email, phone });
  }

  // Update existing contact with new phone if provided
  if (phone) {
    return updateExistingContactWithPhone(billingContact, phone);
  }
};

/**
 * Update existing billing contact with new phone number
 * @param billingContact Existing billing contact
 * @param phone New phone number to add
 */
const updateExistingContactWithPhone = async (billingContact: BillingContact, phone: string): Promise<void> => {
  const PHONES = updateBillingContactPhonesList(billingContact, phone);
  const updatedBillingContact = {
    ...billingContact,
    PHONES,
  };
  return updateBillingContact(updatedBillingContact);
};
